"use client";

import { Card } from "@astryxdesign/core/Card";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { Badge } from "@astryxdesign/core/Badge";
import { Icon, type IconType } from "@astryxdesign/core/Icon";
import { researchIcons } from "@/lib/icons/research-icons";
import type { PlanPackDefinition } from "@/lib/billing/plan-display";

const PREMIUM_ICONS: Record<string, IconType> = {
  enterprise: researchIcons.building,
  "white-label": researchIcons.layers,
};

function premiumIcon(slug: string): IconType {
  return PREMIUM_ICONS[slug] ?? researchIcons.users;
}

type PremiumPlanCardProps = {
  pack: PlanPackDefinition;
  onAction?: () => void;
  href?: string;
  actionLabel?: string;
};

export function PremiumPlanCard({
  pack,
  onAction,
  href,
  actionLabel = "Оставить заявку",
}: PremiumPlanCardProps) {
  const IconCmp = premiumIcon(pack.slug);

  const actionButton = href ? (
    <a href={href} className="mt-1 inline-block">
      <Button label={actionLabel} variant="secondary" />
    </a>
  ) : (
    <Button
      label={actionLabel}
      variant="secondary"
      onClick={onAction}
      className="mt-1"
    />
  );

  return (
    <Card padding={6} width="100%" height="100%" variant="default">
      <HStack gap={5} vAlign="start" width="100%" wrap="wrap">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[color-mix(in_srgb,var(--color-accent-teal,#0d9488)_14%,transparent)] text-[var(--color-accent-teal,#0d9488)]">
          <Icon icon={IconCmp} size="lg" />
        </div>

        <VStack gap={3} align="start" className="min-w-[14rem] flex-1">
          <HStack gap={2} vAlign="center" wrap="wrap">
            <Heading level={3}>{pack.cardTitle}</Heading>
            <Badge label={pack.badgeLabel} variant="neutral" />
          </HStack>
          <Text color="secondary">{pack.cardSubtitle}</Text>
          <Text weight="semibold">Индивидуально</Text>
          <Text type="supporting" color="secondary">
            Цена и условия — после заявки. Свяжемся, выставим счёт, без
            онлайн-покупки.
          </Text>

          <VStack gap={1} align="start" width="100%">
            {pack.features.map((f) => (
              <Text key={f} size="sm" color="secondary">
                ✓ {f}
              </Text>
            ))}
          </VStack>

          {actionButton}
        </VStack>
      </HStack>
    </Card>
  );
}

type PremiumPlansRowProps = {
  plans: PlanPackDefinition[];
  /** Ссылки по slug — для Server Components (функции передавать нельзя). */
  hrefs?: Record<string, string>;
  onInquiry?: (slug: string) => void;
  actionLabel?: string;
  title?: string;
  subtitle?: string;
};

export function PremiumPlansRow({
  plans,
  hrefs,
  onInquiry,
  actionLabel,
  title = "Корпоративные решения",
  subtitle = "Для больших объёмов и встраивания в ваш продукт — обсудим условия и вернёмся с предложением.",
}: PremiumPlansRowProps) {
  if (plans.length === 0) return null;

  return (
    <VStack gap={4} align="start" width="100%">
      <VStack gap={1} align="start">
        <Heading level={3}>{title}</Heading>
        <Text color="secondary">{subtitle}</Text>
      </VStack>
      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
        {plans.map((pack) => (
          <PremiumPlanCard
            key={pack.slug}
            pack={pack}
            href={hrefs?.[pack.slug]}
            onAction={
              onInquiry ? () => onInquiry(pack.slug) : undefined
            }
            actionLabel={actionLabel}
          />
        ))}
      </div>
    </VStack>
  );
}
